import React, { useState, useMemo, useEffect } from 'react';
import styled, { withTheme } from 'styled-components/macro';
import {
  Icon, Text, Spacer, Button
} from 'components/atoms';
import { useDropzone } from 'react-dropzone';
import { themeType, imageType } from 'types';
import PropTypes from 'prop-types';
import { generatePortalImg } from 'services/generators';

const MediaInput = ({
  theme,
  onChange,
  initialImage,
  onEdit,
  name,
  isEditable,
  shouldPreview,
  onClick,
  isInput,
}) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(false);
  const [isHover, setHover] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);
  const [hasRemoved, setHasRemoved] = useState(false);

  useEffect(() => {
    if (hasRemoved && !currentFile) {
      setPreview(null);
      return;
    }
    if (shouldPreview && initialImage && initialImage.src && !currentFile) {
      setPreview(initialImage.src);
    }
    if (shouldPreview && initialImage && initialImage.url && !currentFile) {
      setPreview(
        generatePortalImg({
          src: initialImage.url,
          isLocal:
            initialImage.isLocal === undefined ? true : initialImage.isLocal,
          height: 100,
        })
      );
    } else {
      setPreview(null);
    }
  }, [initialImage, hasRemoved, currentFile, shouldPreview]);

  const hasImage = useMemo(() => !!preview, [preview]);

  const handleUpload = file => {
    onChange(file);
    if (onEdit) onEdit();
  };

  const handleChange = file => {
    if (!file) return;
    if (!file && !currentFile) {
      setPreview(null);
      onChange(null);
      return;
    }
    if (!/\.(jpe?g|png|svg)$/i.test(file.name)) {
      setPreview(null);
      setError(
        'Formato não suportado, apenas são permitidos ficheiros jpg ou png.'
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = event => {
      if (shouldPreview) setPreview(event.target.result);
    };
    reader.readAsDataURL(file);
    setError(null);
    setCurrentFile(file);
    handleUpload(file);
  };

  const onDrop = files => {
    handleChange(files[0]);
  };

  const onRemove = e => {
    setHasRemoved(true);
    e.stopPropagation();
    setPreview(null);
    handleChange(null);
    setCurrentFile(null);
  };

  const color = useMemo(() => {
    if (isHover) return theme.colors.primary;
    if (error) return theme.colors.danger;
    return theme.colors.base;
  }, [
    isHover,
    theme.colors.primary,
    theme.colors.base,
    error,
    theme.colors.danger,
  ]);

  const {
    getRootProps, getInputProps, isDragActive, inputRef
  } = useDropzone({
    onDrop,
  });

  const onEditImage = e => {
    e.stopPropagation();
    if (!isEditable) return;
    if (onEdit) onEdit();
    else {
      inputRef.current.click();
    }
  };

  const computedText = useMemo(() => {
    if (isHover) return 'Upload';
    if (error) return 'Formato inválido, tem de ser .jpg ou .png';
    return isDragActive ? 'Drop here' : 'Upload';
  }, [isDragActive, error, isHover]);

  const computedIsEditable = useMemo(() => isEditable && onEdit !== null, [
    onEdit,
    isEditable,
  ]);

  const rootProps = useMemo(() => (isInput ? getRootProps() : {}), [
    getRootProps,
    isInput,
  ]);

  return (
    <>
      <Preview.Wrapper
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        hasImage={hasImage}
      >
        {isHover && (
          <>
            <Preview.HoverCenter>
              {computedIsEditable ? (
                <>
                  <Icon icon="edit" color="#FFF" />
                  <Text modifiers={['inverse']}>Editar imagem</Text>
                </>
              ) : (
                <>
                  <Icon icon="edit" color="#FFF" />
                  <Text modifiers={['inverse']}>Não editável</Text>
                </>
              )}
            </Preview.HoverCenter>
            <Preview.Hover onClick={onEditImage} isEditable={isEditable}>
              <Button modifiers={['smaller', 'invisible']} onClick={onRemove}>
                <Icon color="#FFF" icon="close" height={16} />
              </Button>
            </Preview.Hover>
          </>
        )}
        <Preview img={preview} />
      </Preview.Wrapper>
      <DropzoneWrapper hasImage={hasImage} onClick={onClick}>
        <Dropzone
          {...rootProps}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          title="Carregar imagem"
          error={error}
          preview={preview}
        >
          <input
            name={name}
            {...getInputProps()}
            accept="image/*"
            onChange={e => handleChange(e.target.files[0])}
          />
          <Spacer bottom="xs3">
            <Icon icon="add_photo" color={color} />
          </Spacer>
          <Text>{computedText}</Text>
        </Dropzone>
      </DropzoneWrapper>
    </>
  );
};

MediaInput.propTypes = {
  theme: themeType.isRequired,
  onChange: PropTypes.func,
  onEdit: PropTypes.func,
  initialImage: imageType,
  isEditable: PropTypes.bool,
  shouldPreview: PropTypes.bool,
  name: PropTypes.string,
  onClick: PropTypes.func,
  isInput: PropTypes.bool,
};

MediaInput.defaultProps = {
  onChange: () => null,
  onEdit: null,
  initialImage: null,
  isEditable: false,
  shouldPreview: false,
  name: '',
  onClick: () => null,
  isInput: true,
};

const DropzoneWrapper = styled.div`
  ${props => props.hasImage && 'display: none'};
  width: 100%;
  height: 100%;
`;

const Preview = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-image: url(${props => props.img});
  background-size: 100% auto;
  background-repeat: no-repeat;
  background-position: center;
`;

Preview.Wrapper = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  border: 1px solid ${props => props.theme.colors.bgDark};
  ${props => !props.hasImage && 'display: none'};
`;

Preview.Hover = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  padding: ${props => props.theme.spacing.xs2};
  z-index: ${props => props.theme.values.zIndex.l};
  top: 0;
  left: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
  cursor: ${props => (props.isEditable ? 'pointer' : 'not-allowed')};
`;

Preview.HoverCenter = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  z-index: ${props => props.theme.values.zIndex.l};
  justify-content: center;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.bgHover};
`;

const Dropzone = styled.div`
  width: 100%;
  height: 100%;
  border: 1px
    ${props => (props.preview
    ? `dashed ${props.theme.colors.greyLighter}`
    : `dashed ${props.theme.colors.base}`)};
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  cursor: pointer;
  text-align: center;
  padding: ${props => props.theme.spacing.xs2};
  position: relative;
  ${Text} {
    color: ${props => (props.error ? props.theme.colors.danger : props.theme.colors.base)};
  }
  &:hover {
    border: 1px
      ${props => (props.preview
    ? `dashed ${props.theme.colors.greyLighter}`
    : `dashed ${props.theme.colors.primary}`)};
    ${Text} {
      color: ${props => props.theme.colors.primary};
    }
  }
  &:focus {
    outline: 0;
  }
`;

export default withTheme(MediaInput);
